import React, { useState, useEffect, useCallback } from 'react';
import { X, Wallet, Link2, ClipboardPaste, CheckCircle, AlertCircle, Loader2, ExternalLink, ChevronLeft, Info } from 'lucide-react';
import { useAppKit, useAppKitAccount, useAppKitState } from '@reown/appkit/react';
import { useChainId } from 'wagmi';
import { SUPPORTED_WALLET_NETWORKS, validateWalletAddress, WalletNetwork } from '../config/wallet';
import { API_BASE_URL } from '../config/api';
import { useTranslation } from 'react-i18next';

// ── Types ──────────────────────────────────────────────────────────────────
export interface WalletAccountData {
  id: number;
  network_name: string;
  network_display_name: string;
  account_label: string;
  wallet_address: string;
  full_display_name: string;
  is_active: boolean;
  last_synced_at: string | null;
}

interface AddWalletModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (accounts: WalletAccountData[]) => void;
  // Called specifically when user chose WalletConnect (modal closes to avoid overlap)
  onWalletConnectInitiated?: () => void;
  // When set, skip type/network picker and go straight to network checklist
  prefilledAddress?: string;
  prefilledType?: 'evm' | 'solana' | 'bitcoin';
  alreadyTrackedNetworks?: string[];
}

type Step = 'method' | 'type' | 'networks' | 'address';
type AddressType = 'evm' | 'solana' | 'bitcoin';

// wagmi chainId → network_name mapping
const CHAIN_ID_TO_NETWORK: Record<number, string> = {
  1: 'ethereum',
  56: 'bsc',
  137: 'polygon',
  42161: 'arbitrum',
  43114: 'avalanche',
  8453: 'base',
  10: 'optimism',
};

const EVM_NETWORKS = SUPPORTED_WALLET_NETWORKS.filter(n => n.addressPrefix === '0x');
const NON_EVM_NETWORKS = SUPPORTED_WALLET_NETWORKS.filter(n => n.addressPrefix !== '0x');

// ── Component ──────────────────────────────────────────────────────────────
const AddWalletModal: React.FC<AddWalletModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  onWalletConnectInitiated,
  prefilledAddress,
  prefilledType,
  alreadyTrackedNetworks = [],
}) => {
  const { t, i18n } = useTranslation();
  const isId = i18n.language === 'id';

  // Reown / Wagmi hooks
  const { open: openReown } = useAppKit();
  const { address: wcAddress, isConnected: wcConnected } = useAppKitAccount();
  const chainId = useChainId();

  // Step flow
  const [step, setStep] = useState<Step>('method');
  const [addressType, setAddressType] = useState<AddressType | null>(null);

  // Network checklist (EVM)
  const [selectedNetworks, setSelectedNetworks] = useState<Set<string>>(new Set());

  // Address + label
  const [walletAddress, setWalletAddress] = useState('');
  const [accountLabel, setAccountLabel] = useState('');
  const [addrError, setAddrError] = useState('');

  // Submission
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [savedCount, setSavedCount] = useState(0);

  // Internal visibility — set false immediately when WC is chosen
  // so our modal disappears before Reown modal opens, without race condition
  const [hidden, setHidden] = useState(false);

  // Reset hidden when modal reopens
  useEffect(() => {
    if (isOpen) setHidden(false);
  }, [isOpen]);

  // Track if we opened Reown from this modal
  const [waitingForWC, setWaitingForWC] = useState(false);
  useEffect(() => {
    if (!isOpen) return;
    if (prefilledAddress && prefilledType) {
      setWalletAddress(prefilledAddress);
      setAddressType(prefilledType);
      if (prefilledType === 'evm') {
        const detected = chainId ? CHAIN_ID_TO_NETWORK[chainId] : 'ethereum';
        const initial = new Set<string>();
        // Try detected chain first, then ethereum, then just pick first untracked EVM network
        if (detected && !alreadyTrackedNetworks.includes(detected)) {
          initial.add(detected);
        } else if (!alreadyTrackedNetworks.includes('ethereum')) {
          initial.add('ethereum');
        } else {
          // All common networks tracked — pick first untracked
          const firstUntracked = EVM_NETWORKS.find(n => !alreadyTrackedNetworks.includes(n.value));
          if (firstUntracked) initial.add(firstUntracked.value);
        }
        setSelectedNetworks(initial);
        setStep('networks');
      } else {
        setStep('address');
      }
    } else {
      setStep('method');
    }
  }, [isOpen, prefilledAddress, prefilledType]);

  // ── WalletConnect connection listener ─────────────────────────────────
  // When wallet connects, auto-submit directly to backend (no extra steps needed)
  useEffect(() => {
    if (!waitingForWC) return;
    if (!wcConnected || !wcAddress) return;

    setWaitingForWC(false);
    const addr = wcAddress.trim();
    const detected = chainId ? CHAIN_ID_TO_NETWORK[chainId] : 'ethereum';
    const network = (detected && !alreadyTrackedNetworks.includes(detected))
      ? detected
      : 'ethereum';

    // Auto-submit directly — no UI needed
    const autoSubmit = async () => {
      setIsLoading(true);
      setError('');
      try {
        const token = localStorage.getItem('auth_token');
        if (!token) throw new Error('Not authenticated');

        const response = await fetch(`${API_BASE_URL}/api/wallet-accounts`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
            Accept: 'application/json',
          },
          body: JSON.stringify({
            network_name: network,
            wallet_address: addr,
          }),
        });

        const data = await response.json();
        if (response.ok || response.status === 409) {
          setSavedCount(1);
          if (data.data) onSuccess([data.data]);
          setTimeout(() => {
            reset();
          }, 1500);
        } else {
          throw new Error(data.message || 'Failed to save wallet');
        }
      } catch (err: any) {
        setError(err.message || 'Something went wrong');
      } finally {
        setIsLoading(false);
      }
    };

    autoSubmit();
  }, [wcConnected, wcAddress, waitingForWC, chainId]);

  const reset = useCallback(() => {
    setStep('method');
    setAddressType(null);
    setSelectedNetworks(new Set());
    setWalletAddress('');
    setAccountLabel('');
    setAddrError('');
    setError('');
    setIsLoading(false);
    setSavedCount(0);
    setWaitingForWC(false);
    setHidden(false);
  }, []);

  const handleClose = () => {
    reset();
    onClose();
  };

  // ── Step: Connect via WalletConnect ──────────────────────────────────
  // Simplified: open Reown, wait for connection, then auto-submit with detected network
  const handleWalletConnect = async () => {
    setHidden(true);
    onWalletConnectInitiated?.();
    onClose();
    await new Promise(resolve => requestAnimationFrame(resolve));
    setWaitingForWC(true);
    try {
      await openReown({ view: 'Connect' });
    } catch {
      setWaitingForWC(false);
    }
  };

  // ── Step: Address type selected ───────────────────────────────────────
  const handleTypeSelect = (type: AddressType) => {
    setAddressType(type);
    if (type === 'evm') {
      // Default: check ethereum
      setSelectedNetworks(new Set(['ethereum']));
      setStep('networks');
    } else {
      setStep('address');
    }
  };

  // ── Step: Network checklist ───────────────────────────────────────────
  const toggleNetwork = (network: string) => {
    if (alreadyTrackedNetworks.includes(network)) return;
    setSelectedNetworks(prev => {
      const next = new Set(prev);
      if (next.has(network)) {
        next.delete(network);
      } else {
        next.add(network);
      }
      return next;
    });
  };

  const handleNetworksContinue = () => {
    if (selectedNetworks.size === 0) return;
    if (prefilledAddress) {
      handleSubmit(prefilledAddress, 'evm');
    } else {
      setStep('address');
    }
  };

  // ── Step: Address input ───────────────────────────────────────────────
  const handleAddressChange = (val: string) => {
    setWalletAddress(val);
    // Validate against the relevant network
    const testNetwork = addressType === 'evm'
      ? 'ethereum' // EVM validation is same for all chains
      : addressType === 'solana' ? 'solana' : 'bitcoin';
    const err = validateWalletAddress(testNetwork, val);
    setAddrError(err || '');
  };

  // ── Submit ─────────────────────────────────────────────────────────────
  const handleSubmit = async (overrideAddress?: string, overrideType?: AddressType) => {
    const addr = (overrideAddress || walletAddress).trim();
    const type = overrideType || addressType;
    const networks = type === 'evm'
      ? Array.from(selectedNetworks)
      : type === 'solana' ? ['solana'] : ['bitcoin'];

    if (networks.length === 0) { setError('Please select at least one network'); return; }
    if (!addr) { setError('Wallet address is missing'); return; }

    const testNet = type === 'evm' ? 'ethereum' : (type ?? 'ethereum');
    const addrErr = validateWalletAddress(testNet, addr);
    if (addrErr) { setAddrError(addrErr); return; }

    setIsLoading(true);
    setError('');

    try {
      const token = localStorage.getItem('auth_token');
      if (!token) throw new Error('Not authenticated');

      const saved: WalletAccountData[] = [];

      for (const network of networks) {
        const response = await fetch(`${API_BASE_URL}/api/wallet-accounts`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
            Accept: 'application/json',
          },
          body: JSON.stringify({
            network_name: network,
            wallet_address: addr,
            account_label: accountLabel || undefined,
          }),
        });

        const data = await response.json();
        // 409 = already exists, treat as success
        if (response.ok || response.status === 409) {
          if (data.data) saved.push(data.data as WalletAccountData);
        } else {
          throw new Error(data.message || `Failed to save ${network}`);
        }
      }

      setSavedCount(saved.length);
      onSuccess(saved);

      setTimeout(() => handleClose(), 1800);
    } catch (err: any) {
      setError(err.message || 'Something went wrong');
    } finally {
      setIsLoading(false);
    }
  };

  // ── Don't render if closed ────────────────────────────────────────────
  if (!isOpen && !waitingForWC) return null;
  if (hidden && !waitingForWC) return null;

  // While waiting for WC connection: render NOTHING — Reown handles its own UI.
  // Don't block Reown with our overlay.
  if (waitingForWC && !isLoading && savedCount === 0) return null;

  // After WC auto-submit succeeds: show a small bottom toast (non-blocking)
  if (savedCount > 0 && !isOpen) {
    return (
      <div className="fixed bottom-6 right-6 z-[9998] bg-[#0D0D0D] border border-green-500/30 rounded-2xl p-5 flex items-start gap-3 shadow-2xl w-72">
        <CheckCircle size={20} className="text-green-400 flex-shrink-0 mt-0.5" />
        <div>
          <p className="text-white font-bold text-sm">Wallet connected!</p>
          <p className="text-neutral-500 text-xs mt-0.5">Syncing transactions in background…</p>
        </div>
      </div>
    );
  }

  // Saving state (after WC connects, before backend responds)
  if (isLoading && !isOpen) {
    return (
      <div className="fixed bottom-6 right-6 z-[9998] bg-[#0D0D0D] border border-neutral-700 rounded-2xl p-5 flex items-start gap-3 shadow-2xl w-72">
        <Loader2 size={20} className="text-purple-400 animate-spin flex-shrink-0 mt-0.5" />
        <div>
          <p className="text-white font-bold text-sm">Saving wallet…</p>
          {error && <p className="text-red-400 text-xs mt-0.5">{error}</p>}
        </div>
      </div>
    );
  }

  const isDone = savedCount > 0;

  // ── Back button logic ─────────────────────────────────────────────────
  const handleBack = () => {
    if (step === 'type') setStep('method');
    else if (step === 'networks') {
      if (prefilledAddress) { handleClose(); }
      else setStep('type');
    }
    else if (step === 'address') {
      if (addressType === 'evm') setStep('networks');
      else setStep('type');
    }
  };

  const selectedNetArr = Array.from(selectedNetworks);

  // ── Render ────────────────────────────────────────────────────────────
  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-[10000] p-4">
      <div className="bg-[#0D0D0D] border border-neutral-800 rounded-2xl w-full max-w-[480px] max-h-[90vh] overflow-y-auto" data-lenis-prevent>

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-5 border-b border-neutral-800">
          <div className="flex items-center gap-3">
            {step !== 'method' && (
              <button onClick={handleBack} className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-neutral-800 transition-colors text-neutral-400 hover:text-white">
                <ChevronLeft size={18} />
              </button>
            )}
            <h2 className="text-base font-bold text-white">
              {step === 'method' && t('addWallet.title')}
              {step === 'type' && t('addWallet.titleSelectType')}
              {step === 'networks' && t('addWallet.titleSelectNetworks')}
              {step === 'address' && t('addWallet.titleEnterAddress')}
            </h2>
          </div>
          <button onClick={handleClose} className="text-neutral-500 hover:text-white transition-colors">
            <X size={20} />
          </button>
        </div>

        <div className="p-6 space-y-4">

          {/* ── Error ── */}
          {error && (
            <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-3 flex items-start gap-2">
              <AlertCircle size={16} className="text-red-500 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-400">{error}</p>
            </div>
          )}

          {/* ── Success ── */}
          {isDone && (
            <div className="bg-green-500/10 border border-green-500/30 rounded-xl p-4 flex items-start gap-3">
              <CheckCircle size={18} className="text-green-500 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-bold text-green-400">{t('addWallet.connected')}</p>
                <p className="text-xs text-green-500/70 mt-0.5">{t('addWallet.syncingBackground')}</p>
                {/* Bilingual extend hint */}
                <p className="text-xs text-neutral-500 mt-2 leading-relaxed">
                  {isId ? t('addWallet.extendNetworksBannerID') : t('addWallet.extendNetworksBanner')}
                </p>
              </div>
            </div>
          )}

          {/* ══════════ STEP: METHOD ══════════ */}
          {step === 'method' && !isDone && (
            <div className="space-y-3">
              <p className="text-sm text-neutral-500">
                {isId ? 'Hubungkan wallet crypto untuk melacak transaksi on-chain Anda secara otomatis.' : 'Connect a crypto wallet to track your on-chain transactions automatically.'}
              </p>

              {/* WalletConnect */}
              <button
                onClick={handleWalletConnect}
                className="w-full flex items-start gap-4 p-4 rounded-xl border border-neutral-700 hover:border-red-600/60 bg-neutral-900/50 hover:bg-neutral-800/80 transition-all group text-left"
              >
                <div className="p-2.5 rounded-xl bg-red-600/10 border border-red-600/20 group-hover:bg-red-600/20 transition-colors flex-shrink-0">
                  <Link2 size={18} className="text-red-500" />
                </div>
                <div>
                  <p className="font-bold text-white text-sm mb-0.5">{t('addWallet.connectWallet')}</p>
                  <p className="text-xs text-neutral-500 leading-relaxed">{t('addWallet.connectWalletDesc')}</p>
                </div>
              </button>

              {/* Manual */}
              <button
                onClick={() => setStep('type')}
                className="w-full flex items-start gap-4 p-4 rounded-xl border border-neutral-700 hover:border-neutral-500 bg-neutral-900/50 hover:bg-neutral-800/80 transition-all group text-left"
              >
                <div className="p-2.5 rounded-xl bg-neutral-800 group-hover:bg-neutral-700 transition-colors flex-shrink-0">
                  <ClipboardPaste size={18} className="text-neutral-300" />
                </div>
                <div>
                  <p className="font-bold text-white text-sm mb-0.5">{t('addWallet.addManually')}</p>
                  <p className="text-xs text-neutral-500 leading-relaxed">{t('addWallet.addManuallyDesc')}</p>
                </div>
              </button>
            </div>
          )}

          {/* ══════════ STEP: ADDRESS TYPE ══════════ */}
          {step === 'type' && !isDone && (
            <div className="space-y-3">
              {[
                { type: 'evm' as AddressType, icon: '⬡', title: t('addWallet.typeEvm'), desc: t('addWallet.typeEvmDesc'), color: '#627EEA' },
                { type: 'solana' as AddressType, icon: '◎', title: t('addWallet.typeSolana'), desc: t('addWallet.typeSolanaDesc'), color: '#9945FF' },
                { type: 'bitcoin' as AddressType, icon: '₿', title: t('addWallet.typeBitcoin'), desc: t('addWallet.typeBitcoinDesc'), color: '#F7931A' },
              ].map(({ type, icon, title, desc, color }) => (
                <button
                  key={type}
                  onClick={() => handleTypeSelect(type)}
                  className="w-full flex items-center gap-4 p-4 rounded-xl border border-neutral-700 hover:border-neutral-500 bg-neutral-900/50 hover:bg-neutral-800/80 transition-all text-left group"
                >
                  <div
                    className="w-10 h-10 rounded-xl flex items-center justify-center text-lg font-bold flex-shrink-0"
                    style={{ backgroundColor: `${color}20`, color }}
                  >
                    {icon}
                  </div>
                  <div>
                    <p className="font-bold text-white text-sm">{title}</p>
                    <p className="text-xs text-neutral-500 mt-0.5">{desc}</p>
                  </div>
                </button>
              ))}
            </div>
          )}

          {/* ══════════ STEP: NETWORK CHECKLIST (EVM) ══════════ */}
          {step === 'networks' && !isDone && (
            <div className="space-y-4">
              <div>
                <p className="text-sm font-medium text-neutral-300 mb-1">{t('addWallet.networkChecklistTitle')}</p>
                <p className="text-xs text-neutral-500">{t('addWallet.networkChecklistHint')}</p>
              </div>

              {/* If address was pre-filled, show it */}
              {walletAddress && (
                <div className="bg-neutral-900 border border-neutral-800 rounded-xl px-4 py-3">
                  <p className="text-[10px] text-neutral-600 uppercase tracking-widest mb-1">Address</p>
                  <p className="text-sm font-mono text-neutral-300 truncate">{walletAddress}</p>
                </div>
              )}

              <div className="space-y-2">
                {EVM_NETWORKS.map(net => {
                  const isTracked = alreadyTrackedNetworks.includes(net.value);
                  const isChecked = selectedNetworks.has(net.value);
                  return (
                    <button
                      key={net.value}
                      type="button"
                      onClick={() => toggleNetwork(net.value)}
                      disabled={isTracked}
                      className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl border transition-all text-left ${
                        isTracked
                          ? 'border-neutral-800 bg-neutral-900/30 opacity-50 cursor-not-allowed'
                          : isChecked
                          ? 'border-red-600/60 bg-red-600/10'
                          : 'border-neutral-700 bg-neutral-900/50 hover:border-neutral-600'
                      }`}
                    >
                      <div
                        className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                        style={{ backgroundColor: `${net.color}20` }}
                      >
                        <img src={net.icon} alt={net.label} className="w-5 h-5 object-contain" onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-white">{net.label}</p>
                        {isTracked && <p className="text-xs text-neutral-500">{t('addWallet.alreadyTracked')}</p>}
                      </div>
                      <div className={`w-5 h-5 rounded-md border flex items-center justify-center flex-shrink-0 transition-all ${
                        isTracked ? 'border-neutral-700 bg-neutral-800' :
                        isChecked ? 'border-red-600 bg-red-600' : 'border-neutral-600'
                      }`}>
                        {(isChecked || isTracked) && <CheckCircle size={12} className="text-white" />}
                      </div>
                    </button>
                  );
                })}
              </div>

              <button
                onClick={handleNetworksContinue}
                disabled={selectedNetworks.size === 0}
                className="w-full py-3 bg-red-600 hover:bg-red-500 text-white font-bold rounded-xl transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm"
              >
                {prefilledAddress
                  ? (isLoading ? <Loader2 size={16} className="animate-spin mx-auto" /> : `Track ${selectedNetworks.size} Network${selectedNetworks.size !== 1 ? 's' : ''}`)
                  : `Continue (${selectedNetworks.size} selected)`
                }
              </button>
            </div>
          )}

          {/* ══════════ STEP: ADDRESS INPUT ══════════ */}
          {step === 'address' && !isDone && (
            <div className="space-y-4">
              {/* Selected networks summary (EVM) */}
              {addressType === 'evm' && selectedNetArr.length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                  {selectedNetArr.map(n => {
                    const net = SUPPORTED_WALLET_NETWORKS.find(w => w.value === n);
                    return net ? (
                      <span key={n} className="flex items-center gap-1 px-2 py-1 rounded-lg bg-neutral-800 border border-neutral-700 text-xs font-medium text-neutral-300">
                        <img src={net.icon} alt={net.label} className="w-3.5 h-3.5" />
                        {net.label}
                      </span>
                    ) : null;
                  })}
                </div>
              )}

              {/* Address input */}
              <div>
                <label className="block text-sm font-medium text-neutral-300 mb-2">{t('addWallet.addressLabel')} *</label>
                <div className="relative">
                  <input
                    type="text"
                    value={walletAddress}
                    onChange={e => handleAddressChange(e.target.value)}
                    placeholder={
                      addressType === 'evm' ? '0x...' :
                      addressType === 'solana' ? 'Base58 address...' :
                      '1... or bc1...'
                    }
                    className={`w-full bg-neutral-900 border rounded-xl px-4 py-3 text-white placeholder-neutral-600 focus:outline-none font-mono text-sm pr-10 ${
                      addrError ? 'border-red-500' : 'border-neutral-700 focus:border-red-500'
                    }`}
                  />
                  {walletAddress && !addrError && (
                    <CheckCircle size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-green-500" />
                  )}
                </div>
                {addrError && <p className="text-xs text-red-400 mt-1">{addrError}</p>}
              </div>

              {/* Label */}
              <div>
                <label className="block text-sm font-medium text-neutral-300 mb-2">{t('addWallet.labelOptional')}</label>
                <input
                  type="text"
                  value={accountLabel}
                  onChange={e => setAccountLabel(e.target.value)}
                  placeholder={t('addWallet.labelPlaceholder')}
                  className="w-full bg-neutral-900 border border-neutral-700 rounded-xl px-4 py-3 text-white placeholder-neutral-600 focus:outline-none focus:border-red-500 text-sm"
                />
              </div>

              {/* Read-only notice */}
              <div className="flex gap-3 bg-neutral-900/60 rounded-xl p-4">
                <Info size={16} className="text-neutral-500 flex-shrink-0 mt-0.5" />
                <p className="text-xs text-neutral-500 leading-relaxed">{t('addWallet.readOnlyNotice')}</p>
              </div>

              {/* Submit */}
              <button
                onClick={() => handleSubmit(undefined, addressType ?? undefined)}
                disabled={isLoading || !!addrError || !walletAddress}
                className="w-full flex items-center justify-center gap-2 bg-red-600 hover:bg-red-500 text-white font-bold py-3 rounded-xl transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm"
              >
                {isLoading ? (
                  <><Loader2 size={16} className="animate-spin" /> {t('addWallet.connecting')}</>
                ) : (
                  <><Wallet size={16} /> {t('addWallet.connectBtn')}</>
                )}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AddWalletModal;
